import type { RouteDef } from '@/app/routes'
import type { WrittenPage } from '../sections'
import { LINKS } from './links'

/**
 * Our Values — its own page again.
 *
 * Asked for 2026-08-31 with a reference image: About Us should carry only the
 * picture, the line and an "Our Values" button, and the button (or the picture)
 * should open the values themselves. So the eight values moved off About Us and
 * on to this page, word for word — they are digitopia.co's own copy, kept whole
 * rather than summarised.
 *
 * The slug is the one they had on the old site
 * (`digitopia.co/about/our-values`), so an inbound link to it still lands on
 * the values rather than somewhere near them.
 *
 * **Laid out to match that old page exactly**, asked for the same day with three
 * screenshots of it: a centred title-only opener, then one full-width grey card
 * per value with the title in blue on the left and each sentence on its own line
 * on the right (`features` `variant: 'rows'`). The reference's own "Let's Get in
 * Touch!" band at the foot was explicitly excluded — *"contact us kısmı hariç o
 * bizim tasarımımız var zaten"* — so the `cta` below stays ours.
 */
export const route: RouteDef = {
  id: 'ourValues',
  path: 'about/our-values',
  priority: 0.7,
}

export const ourValues: WrittenPage = {
  navLabel: 'Our Values',
  title: 'Our Values',
  description:
    'The eight values Digitopians hold each other to: humble but courageous, empowered accountability, yes we can, challenge the status quo, share to enable, have fun, hungry for learning, work for a cause.',
  sections: [
    {
      kind: 'hero',
      headline: 'Our Values',
      align: 'center',
    },
    {
      kind: 'features',
      variant: 'rows',
      items: [
        {
          title: 'Humble, but courageous.',
          lines: [
            'We are humble, but don’t be mistaken…',
            'We dream big.',
            'We are confident.',
            'And we are courageous.',
          ],
        },
        {
          title: 'Empowered Accountability',
          lines: [
            'We think each and every colleague of ours is responsible for delivering value.',
            'We feel accountable for everything we do, and we seek for empowerment while delivering our responsibilities.',
            'We don’t let things slip from our hands, we grab them and try until we have succeeded.',
          ],
        },
        {
          title: 'Yes, we can!',
          lines: [
            'We always find ways to accomplish our goals.',
            'When we face a challenge, we resolve it.',
            'When we hit an obstacle, we overcome it.',
            'When we don’t know something, we simply learn it.',
          ],
        },
        {
          title: 'Challenge the status quo.',
          lines: [
            'We don’t accept what is served as status quo.',
            'We are here to change.',
            'We experiment with care.',
            'We are adventurous to better our world.',
            'We seek creativity in life, in people, in delivery.',
          ],
        },
        {
          title: 'Share to enable',
          lines: [
            'Collaborative knowledge is the ultimate experience we can get.',
            'As individuals we do have specific knowledge and experience.',
            'We think that when we share, we enable others.',
            'In return they enable us.',
            'The return on this process is enormous as we all enable each other and gain experience through sharing.',
          ],
        },
        {
          title: 'Have fun!',
          lines: [
            'Do you really need an explanation for this? 😊',
            'We are spending most of our time at work.',
            'Why don’t you just enjoy it?',
            'Is anything missing? Is something bothering you? Just solve it.',
            'And let the fun continue!',
          ],
        },
        {
          title: 'Hungry for learning',
          lines: [
            'We always feel the hunger for knowledge.',
            'We read, we grow and we understand, internalize, act, execute, and enhance everything we do.',
            'We never stop learning.',
          ],
        },
        {
          title: 'Work for a cause',
          lines: [
            'We don’t just work; we work for a cause.',
            'We are in a constant search for meaning in our work.',
            'We shall not do it only for money.',
            'We are here to serve people and the world we live in.',
          ],
        },
      ],
    },
    {
      kind: 'cta',
      heading: 'Become a Digitopian.',
      sub: 'We hire for courage, ownership and curiosity. The rest is learnable, and we will help you learn it.',
      tone: 'band',
      headingHref: LINKS.careers,
      ctas: [{ label: 'See open roles', href: LINKS.careers }],
    },
  ],
}
